import React from "react";
import {
  CellContext,
  ColumnDef,
  createColumnHelper
} from "@tanstack/react-table";
import _ from "lodash";
import styled, { css } from "styled-components";

import { customCreateColumnHelper } from "src/core/dashboard/components/widgets/table/customColumnHelper";
import { IProcessJob } from "src/core/process-job/api/processJobs.api";
import { Text } from "src/library/components/Text";
import { Colors } from "src/library/utils/theme";

import {
  ITerminology,
  splitAndCaplitalise
} from "../../api";

export interface IProcessJobColumns {
  actionButton: (info: CellContext<IProcessJob, unknown>) => React.ReactNode;
  terminology?: ITerminology,
  excludedColumns?: ProcessJobAvailableTableColumn[]
  nameHeaderOptions?: Array<{
    text: string;
    value: string;
  }>

}

export type ProcessJobAvailableTableColumn = keyof AvailableProcessJobColumns;

// used to determine what columns can be rendered
interface AvailableProcessJobColumns {
  status: ColumnDef<IProcessJob, unknown>,
  statusMessage: ColumnDef<IProcessJob, unknown>,
  name: ColumnDef<IProcessJob, unknown>,
  createdAt: ColumnDef<IProcessJob, unknown>,
  startedAt: ColumnDef<IProcessJob, unknown>,
  finishedAt: ColumnDef<IProcessJob, unknown>,
  triggeredByType: ColumnDef<IProcessJob, unknown>,
  triggeredBy: ColumnDef<IProcessJob, unknown>,
  actions: ColumnDef<IProcessJob, unknown>
}

export const getProcessJobColumns = (params: IProcessJobColumns): Array<ColumnDef<IProcessJob, unknown>> => {
  const customColumnHelper = customCreateColumnHelper<IProcessJob>();
  const columnHelper = createColumnHelper<IProcessJob>();

  const allColumns: AvailableProcessJobColumns = {
    status: customColumnHelper.standardTextColumn(columnHelper, "status", {
      //set the status colour
      cell: info => {
        const humanReadable = splitAndCaplitalise(info.row.original.status, "_");
        let color: keyof Colors | "primary" = "primary";

        switch (info.row.original.status) {
          case "error":
            color = "error";
            break;
          case "in_progress":
          case "initialising":
          case "queued":
          case "part_error":
            color = "warn";
            break;
          case "success":
            color = "success";
        }

        return (
          <StatusWrapper color={color}>
            <Text>
              {humanReadable}
            </Text>
          </StatusWrapper>
        );
      },
      meta: {
        filter: {
          filterType: "select",
          options: [
            {
              text: "Error",
              value: "error"
            },
            {
              text: "Part Error",
              value: "part_error"
            },
            {
              text: "Success",
              value: "success"
            },
            {
              text: "In Progress",
              value: "in_progress"
            },
            {
              text: "Initialising",
              value: "initialising"
            },
            {
              text: "queued",
              value: "Queued"
            }
          ]
        }
      }
    }),
    statusMessage: customColumnHelper.standardTextColumn(columnHelper, "statusMessage", { header: "Message" }),
    name: customColumnHelper.standardTextColumn(columnHelper, "name", {
      enableColumnFilter: params.nameHeaderOptions ? true : false,
      cell: info => {
        const humanReadable = splitAndCaplitalise(info.row.original.name, "-");

        return (
          <Text>
            {humanReadable}
          </Text>
        );
      },
      meta: {
        filter: {
          filterType: "select",
          options: params.nameHeaderOptions
        }
      }
    }),
    createdAt: customColumnHelper.dateColumn(columnHelper, "addedAt", {
      header: "Added At",
      template: "datetime"
    }),
    startedAt: customColumnHelper.dateColumn(columnHelper, "initialisedAt", {
      header: "Initialised At",
      template: "datetime"
    }),
    finishedAt: customColumnHelper.dateColumn(columnHelper, "finishedAt", {
      header: "Finished At",
      template: "datetime"
    }),
    triggeredBy: customColumnHelper.standardTextColumn(columnHelper, "triggeredByUser.email", {
      header: "Triggered By",
      size: 275,
      enableSorting: false,
      cell: info => {
        const user = info.row.original.triggeredByUser?.email ? info.row.original.triggeredByUser?.email : "";

        return (
          <Text>
            {user}
          </Text>
        );
      }
    }),
    triggeredByType: customColumnHelper.standardTextColumn(columnHelper, "triggeredByType", {
      enableSorting: false,
      cell: info => {
        const type = _.startCase(info.row.original.triggeredByType);

        return (
          <Text>
            {type}
          </Text>
        );
      },
      header: "Trigger Type",
      meta: {
        filter: {
          filterType: "select",
          options: [
            {
              text: "User",
              value: "user"
            },
            {
              text: "System",
              value: "system"
            }
          ]
        }
      }
    }),
    actions: customColumnHelper.actionColumn(columnHelper, params.actionButton, {})
  };

  if (params.excludedColumns) {
    const result = allColumns;

    for (const col of params.excludedColumns) {
      delete result[ col ];
    }

    return Object.values(result);
  } else {
    return Object.values(allColumns);
  }
};

interface StatusWrapperProps {
  color: string
}

const StatusWrapper = styled.div<StatusWrapperProps>`
  ${({ theme, color }) => css`
    span {
      color: ${theme.colors[ color ]}
    }
`}
`;